/* eslint-env node,browser */
import _ from 'lodash';
import Combinatorics from 'js-combinatorics';
import shortid from 'shortid';

import SharedConfig from 'shared/config';
import { DEVMODE } from 'shared/util';

import SimcInput, { COMMANDS_GEAR_ALIASES } from 'shared/simcinput';
import {
  itemSocketInfo,
  isLegionLegendary,
  getWeaponHands,
  isWeaponEnchantable,
  gemInfo,
  canRollSocket,
} from 'shared/items';
import { getSpecInfo } from 'shared/character';
import { talentsArmoryToSimc } from 'shared/talents';
import { parseAzeriteEssences, stringifyAzeriteEssences } from 'shared/azeriteEssences';
import { getSoulbind, getCovenant, parseSoulbindSimcString } from 'shared/shadowlands';
import GameData from 'shared/gameData';

function getNow() {
  if (typeof window !== 'undefined' && window.performance) {
    return window.performance.now();
  }
  return Date.now();
}

// eslint-disable-next-line no-unused-vars
function devLog(...args) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
}
// eslint-disable-next-line no-unused-vars
function devTable(data) {
  if (DEVMODE) {
    // eslint-disable-next-line no-console
    console.table(data);
  }
}

export function numLegendaries(items) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      return res + numLegendaries(it);
    }
    if (isLegionLegendary(it)) {
      return res + 1;
    }
    return res;
  }, 0);
}

export function numItemSet(items, itemSetIds) {
  return _.reduce(items, (res, it) => {
    if (_.isArray(it)) {
      // skip rings/trinkets for now
      // TODO: if this needs to be done at some point, need to merge the results from a recursive call
      return res;
    }
    const { itemSetId } = it;
    if (itemSetId && _.includes(itemSetIds, itemSetId)) {
      if (!res[itemSetId]) {
        res[itemSetId] = 0;
      }
      res[itemSetId] += 1;
    }
    return res;
  }, {});
}

export function inputSource(input) {
  if (input) {
    if (input.match(/\[Profile\]/)) {
      return 'simpermut';
    }
    return 'simc';
  }
  return null;
}

export function transformInputToData(input) {
  if (!input) {
    return null;
  }
  let data = SimcInput.parse(input);
  if (inputSource(input) === 'simpermut') {
    const name = SimcInput.find(data, 'profilename');
    const charClass = SimcInput.find(data, 'class');

    data = SimcInput.reject(data, '[Profile]');
    data = SimcInput.reject(data, 'profilename');
    data = SimcInput.reject(data, 'profileid');
    data = SimcInput.reject(data, 'class');
    data = SimcInput.reject(data, 'position');
    data = SimcInput.reject(data, 'other');
    data = SimcInput.reject(data, '[Gear]');
    data = SimcInput.prepend(data, charClass, name);
    data = SimcInput.prependComment(data, 'Input from SimPermut');

    // handle inconsistent 2 ring/trinket autosimc output
    // merge the two items into finger1/trinket1 like /simc gear from bags
    const finger1 = SimcInput.find(data, 'finger1');
    if (finger1.split('|').length === 1) {
      const finger2 = SimcInput.find(data, 'finger2');
      data = SimcInput.set(data, 'finger1', [finger1, finger2].join('|'));
    }
    const trinket1 = SimcInput.find(data, 'trinket1');
    if (trinket1.split('|').length === 1) {
      const trinket2 = SimcInput.find(data, 'trinket2');
      data = SimcInput.set(data, 'trinket1', [trinket1, trinket2].join('|'));
    }
    data = SimcInput.reject(data, 'finger2');
    data = SimcInput.reject(data, 'trinket2');
  }
  return data;
}

function parseItemAttributes(item) {
  const rawAttrs = item.split(',');
  const attributes = _.fromPairs(_.tail(rawAttrs).map(attr => attr.split('=')));
  return attributes;
}

const SOURCE_MAP = {
  gear_from_bags: 'bag',
  weekly_reward_choices: 'weekly_reward',
  linked_gear: 'item_link',
};

export function makeGearOptions(data) {
  if (!data || data.length === 0) {
    return {};
  }
  const options = {};
  // simpermut has the same data in finger1 and finger2. same for trinkets
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist', 'legs', 'feet',
    'finger1', 'finger2', 'trinket1', 'trinket2', 'main_hand', 'off_hand',
    'unknown',
  ];

  _.each(slots, slot => {
    options[slot] = [];
    const line = SimcInput.find(data, slot);
    if (line) {
      // handles both normal simc equipped gear input and all simpermut gear input
      const items = line.split('|');
      _.each(items, item => {
        const attributes = parseItemAttributes(item);
        // for simpermut, treat everything as equipped to ensure all items are checked on init
        attributes.equipped = true;
        options[slot].push(attributes);
      });
    }
  });

  // find items in comments
  let source;
  _.each(
    _.filter(data, line => line.comment),
    line => {
      const trimmed = _.trim(line.comment);
      // try to parse
      const [command, ...rest] = trimmed.split('=');
      const slot = COMMANDS_GEAR_ALIASES[command] || command;
      if (_.includes(slots, slot)) {
        const rawAttrs = rest.join('=');
        const attrs = parseItemAttributes(rawAttrs);
        attrs.source = source;
        options[slot].push(attrs);
      } else if (_.startsWith(trimmed, '##')) {
        // section delimeter
        source = SOURCE_MAP[_.snakeCase(_.trim(trimmed, '# '))] || 'unknown';
      }
    },
  );

  // merge rings and trinkets

  options.rings = _.union(options.finger1, options.finger2);
  delete options.finger1;
  delete options.finger2;

  options.trinkets = _.union(options.trinket1, options.trinket2);
  delete options.trinket1;
  delete options.trinket2;

  return options;
}

const slotHasAzeritePiece = items => {
  const numAzeritePieces = _.size(_.filter(items, i => i.azeritePowerSetId));
  return numAzeritePieces > 0;
};

export function isGearValidForOptimize(gear) {
  const inclusiveMinimums = {
    head: 1,
    neck: 1,
    shoulder: 1,
    back: 1,
    chest: 1,
    wrist: 1,
    hands: 1,
    waist: 1,
    legs: 1,
    feet: 1,
    main_hand: 1,
    rings: 2,
    trinkets: 2,
  };

  const valid = _.reduce(
    _.mapValues(inclusiveMinimums, (value, slot) => {
      const result = gear[slot] && (gear[slot].length >= value || slotHasAzeritePiece(gear[slot]));
      return result;
    }),
    (result, aboveMin) => result && aboveMin,
    true,
  );

  return valid;
}

export function getSelectedGear(
  gear,
  selected,
  selectedAzeritePowers,
) {
  // expand azerite and do some other baseline work of populating indexes
  const result = _.mapValues(gear, (items, slot) => {
    const slotSelected = _.filter(
      // save original index
      _.map(items, (item, index) => {
        return {
          ...item,
          origIdx: index,
          selectedIdx: index,
        };
      }),
      // filter selected items
      (item, index) => _.includes(selected[slot], index),
    );

    // expand/replace azerite gear with power combinations
    const slotItems = [];

    _.each(slotSelected, item => {
      const azeritePowers = _.get(_.find(selectedAzeritePowers[slot], { idx: item.origIdx }), 'powers');

      // if no powers selected or a normal item
      if (!azeritePowers || azeritePowers.length === 0) {
        // push the base item
        slotItems.push(item);
      } else {
        // transform az powers back into tiered array for combo making
        const powers = _.map(azeritePowers, power => _.find(item.azeritePowers, { powerId: power }));
        const powersByTier = _.toArray(_.groupBy(powers, 'tier'));
        const powerCombos = Combinatorics.cartesianProduct(...powersByTier).toArray();

        let equippedPowers = [];
        if (item.azerite_powers) {
          equippedPowers = new Set(_.map(item.azerite_powers.split('/'), p => parseInt(p, 10)));
        }

        _.each(powerCombos, powerCombo => {
          const powerList = _.map(_.reverse(powerCombo), 'powerId');
          const newIdx = slotItems.length;
          slotItems.push({
            ...item,
            // make sure we save the new idx for this combo
            origIdx: newIdx,
            equipped: item.equipped && _.isEqual(equippedPowers, new Set(powerList)),
            azerite_powers: powerList.join('/'),
          });
        });
      }
    });
    return slotItems;
  });

  return result;
}

export function applyGemEnchants(
  slot,
  item,
  gem,
  cloakEnchant,
  ringEnchant,
  neckEnchant,
  weaponEnchant,
  weaponOffhandEnchant,
  replaceExisting,
) {
  const applyEnchant = {
    neck: neckEnchant,
    rings: ringEnchant,
    finger1: ringEnchant,
    finger2: ringEnchant,
    back: cloakEnchant,
    main_hand: weaponEnchant,
    off_hand: weaponOffhandEnchant,
  };

  const it = _.cloneDeep(item);

  const isArtifact = item.quality === 6;

  // generate socket info
  const sockets = itemSocketInfo(item);

  // don't swap artifact gems
  if (sockets.total > 0 && !isArtifact) {
    const numEmptySockets = sockets.total - sockets.filled;
    if (replaceExisting || numEmptySockets > 0) {
      const gems = replaceExisting ? [] : sockets.gemIds;
      const toFill = sockets.total - gems.length;
      _.each(_.range(0, toFill), () => gems.push(gem));
      it.gem_id = _.join(gems, '/');
    }
  }

  // ensure enchants are populated
  const enchantableSlot = _.includes(_.keys(applyEnchant), slot);
  if (enchantableSlot && !isArtifact) {
    if (!_.includes(['main_hand', 'off_hand'], slot) || isWeaponEnchantable(it)) {
      if (replaceExisting || !item.enchant_id) {
        it.enchant_id = applyEnchant[slot];
      }
    }
  }

  return it;
}

export function getTalentCombinations(selectedTalents) {
  let selected = selectedTalents;
  if (_.isEmpty(selected) || !_.isArray(selected)) {
    selected = [
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
      [false, false, false],
    ];
  }
  const talentOptions = _.map(
    selected,
    cols => {
      const trimmed = _.reject(
        _.map(cols, (t, idx) => t && idx),
        t => t === false,
      );
      if (trimmed.length === 0) {
        return ['.'];
      }
      return trimmed;
    },
  );

  const talentCombos = _.map(
    Combinatorics.cartesianProduct(...talentOptions).toArray(),
    arr => talentsArmoryToSimc(arr.join('')),
  );

  return talentCombos;
}

function getGemCombinationsDirect(selectedGems, forceMainStat, numSockets) {
  if (_.size(selectedGems) === 0 || numSockets === 0) {
    return [];
  }

  if (numSockets === 1) {
    return _.map(selectedGems, g => [g.enhancementId]);
  }

  const results = [];

  for (let i = 0; i < selectedGems.length; i += 1) {
    const fixedGem = selectedGems[i];

    let nextGems = _.drop(selectedGems, i);

    if (forceMainStat && !fixedGem.unique) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // drop from next slot recusive call
    if (fixedGem.unique) {
      nextGems = _.reject(nextGems, { enhancementId: fixedGem.enhancementId });
    }

    const subCombos = getGemCombinationsDirect(nextGems, false, numSockets - 1);
    _.each(subCombos, combo => {
      results.push([
        fixedGem.enhancementId,
        ...combo,
      ]);
    });
  }

  return results;
}

export function getGemCombinations(selectedGems, forceMainStat, numSockets) {
  const gems = _.reverse(_.sortBy(_.map(selectedGems, g => ({
    ...g,
    unique: _.get(_.find(gemInfo, { id: g.enhancementId }), 'unique', false),
  })), 'unique'));

  // add a check to make sure that a main stat gem is actually selected before applying forceMainStat
  const forceMain = forceMainStat && Boolean(_.find(gems, g => g.unique));

  const validGemSets = getGemCombinationsDirect(gems, forceMain, numSockets);

  return validGemSets;
}

export function getEssenceCombinations(selectedEssences, passiveEssences) {
  let essenceCombos;
  const essenceSlotsOptions = _.groupBy(selectedEssences, 'slot');

  if (_.size(selectedEssences) > 0) {
    const rawEssenceCombos = Combinatorics.cartesianProduct(..._.values(essenceSlotsOptions));

    // locking slot 1 is handled upstream - frontend just populates slot 1 with the locked essence so this code
    // doesn't have to think about it

    // check for invalid essence configs:
    //  * Essence used multiple times
    const minorCombinations = new Set();
    const activeCombos = rawEssenceCombos.filter(combo => {
      // check for dupes
      const seen = new Set();
      for (let i = 0; i < combo.length; i += 1) {
        if (seen.has(combo[i].essenceId)) {
          return false;
        }
        seen.add(combo[i].essenceId);
      }
      // grab and sort slot 1 and 2 and reject if they are already seen
      if (combo.length > 2) {
        const minorKey = [
          combo[0].essenceId,
          ..._.map(_.reject(combo, { slot: 0 }), 'essenceId').sort(),
        ].join('/');
        if (minorCombinations.has(minorKey)) {
          return false;
        }
        minorCombinations.add(minorKey);
      }
      return true;
    });

    essenceCombos = _.map(activeCombos, combo => {
      const essenceStr = stringifyAzeriteEssences(parseAzeriteEssences(_.union(
        _.map(combo, c => `${c.essenceId}:${c.rank}`),
        passiveEssences,
      ).join('/')));
      return {
        corruptionReduction: 0,
        str: essenceStr,
      };
    });
  }
  return essenceCombos;
}


// TODO: Lot of room for optimization here if needed
const getSoulbindCombos = (soulbinds, soulbindGroups, conduits, conduitRankOverride, unlockedTraits, allSoulbinds) => {
  const combos = [];

  const comboConduitMap = {
    endurance: 0,
    finesse: 1,
    potency: 2,
  };

  // decorate conduits with full data
  const conduitsDecorated = _.map(conduits, conduit => {
    return {
      ..._.find(GameData.Conduits, { id: conduit.id }),
      ...conduit,
    };
  });

  const groups = _.groupBy(soulbindGroups, 'soulbindId');

  // iterate over each soulbind
  _.each(soulbinds, soulbindId => {
    const soulbind = getSoulbind(soulbindId);
    const group = groups[soulbindId];
    const byRow = [];

    // group choices by row
    _.each(group, g => {
      if (!byRow[g.row]) {
        byRow[g.row] = [];
      }
      byRow[g.row].push({
        ...g,
        talents: _.filter(soulbind.talents, talent => {
          const unlockKey = _.join([soulbind.id, talent.tier], ':');
          // if unlockedTraits is not set, that means an older version of the frontend code
          // that should skip this check
          if (allSoulbinds || !unlockedTraits || _.includes(unlockedTraits, unlockKey)) {
            return _.isEqual(talent.choiceIndex, [g.row, g.col]);
          }
          return false;
        }),
      });
    });

    // no path selected
    if (byRow.length === 0) {
      return;
    }

    // clean up rows that are not chosen
    // TODO: better handling of this? UI disallow unselecting items if there's a next row selected?
    const rows = _.filter(byRow);

    // determine the possible paths without conduits
    const basePaths = Combinatorics.cartesianProduct(...rows).toArray();

    _.each(basePaths, path => {
      // count up the conduit slots in this path
      const conduitSlots = _.reduce(path, (result, choice) => {
        _.each(choice.talents, talent => {
          if (talent.conduitType) {
            // eslint-disable-next-line no-param-reassign
            result[talent.conduitType] += 1;
          }
        });
        return result;
      }, { endurance: 0, finesse: 0, potency: 0 });

      // determine valid conduit combinations for this slot layout
      const conduitCombos = _.mapValues(conduitSlots, (num, slot) => {
        const matchingConduits = _.filter(conduitsDecorated, { type: slot });
        if (matchingConduits.length < num) {
          // TODO: Path will have empty conduits
          return [null];
        }
        if (matchingConduits.length > 0 && num > 0) {
          const cc = Combinatorics.combination(matchingConduits, num).toArray();
          return cc;
        }
        return [null];
      });

      // create all conduit variations for this path
      const conduitLayouts = Combinatorics.cartesianProduct(..._.values(conduitCombos)).toArray();

      _.each(conduitLayouts, layout => {
        // keep track of which next conduit to slot in
        const conduitIndex = {
          endurance: 0,
          finesse: 0,
          potency: 0,
        };
        const soulbindParts = [];
        const pathWithConduits = _.map(path, choice => {
          // there could be multiple conduits in this path
          const pathConduits = [];
          _.each(_.filter(_.map(choice.talents, 'conduitType')), conduitType => {
            if (conduitType) {
              const conduitsToUse = layout[comboConduitMap[conduitType]];
              if (conduitsToUse) {
                const conduit = conduitsToUse[conduitIndex[conduitType]];
                conduitIndex[conduitType] += 1;
                if (conduit) {
                  pathConduits.push(conduit);
                }
              }
            }
          });
          return {
            ...choice,
            conduits: pathConduits,
          };
        });
        _.each(pathWithConduits, choice => {
          _.each(choice.talents, talent => {
            if (talent.conduitType) {
              const conduitToUse = _.find(choice.conduits, { type: talent.conduitType });
              if (conduitToUse) {
                const rank = conduitRankOverride || conduitToUse.rank;
                soulbindParts.push(`${conduitToUse.id}:${rank}`);
              } else {
                soulbindParts.push('');
              }
            } else {
              soulbindParts.push(`${talent.spellId}`);
            }
          });
        });
        const key = _.join([soulbind.key, soulbind.id], ':');
        const soulbindStr = _.join([key, _.join(soulbindParts, '/')], ',');
        combos.push({
          str: soulbindStr,
          path: pathWithConduits,
          covenant: getCovenant(soulbind.covenantId),
        });
      });
    });
  });

  return combos.length > 0 ? combos : null;
};

const getParsed = item => {
  if (item) {
    let gem;
    let gemIsUnique;
    const gemId = parseInt(item.gem_id, 10) || 0;
    if (gemId) {
      gem = _.find(gemInfo, { id: gemId });
      gemIsUnique = Boolean(_.get(gem, 'unique'));
    }
    return {
      azerite_powers: _.filter(_.map(_.split(item.azerite_powers, '/'), p => parseInt(p, 10))),
      gem_id: gemId,
      enchant_id: parseInt(item.enchant_id, 10) || 0,
      gem,
      gemIsUnique,
    };
  }
  return undefined;
};
const makeNewCacheItem = item => {
  return {
    ...item,
    cacheGuid: shortid.generate(),
  };
};
const decorateItem = item => {
  if (item) {
    return {
      ...item,
      parsed: getParsed(item),
      socketInfo: itemSocketInfo(item),
    };
  }
  return null;
};

export function getRawCombinations(
  gear,
  gem,
  cloakEnchant,
  ringEnchant,
  neckEnchant,
  weaponEnchant,
  weaponOffhandEnchant,
  replaceExisting,
  classId,
  specName,
  selectedTalents,
  lockedTalents,
  selectedEnhancements,
  enhancementReplace,
  selectedEssences,
  passiveEssences,
  addSocketNum,
  addSocketGem,
  soulbinds,
  soulbindGroups,
  conduits,
  conduitRankOverride,
  unlockedSoulbindTraits,
  allSoulbinds,
) {
  const slots = [
    'head', 'neck', 'shoulder', 'back', 'chest', 'wrist', 'hands', 'waist',
    'legs', 'feet', 'rings', 'trinkets', 'main_hand', 'off_hand',
  ];
  if (!gear) {
    return [];
  }
  const requiredSlots = _.pull(slots, 'off_hand');
  const gearPopulated = _.reduce(requiredSlots, (res, slot) => res && gear[slot] && gear[slot].length > 0, true);
  if (!gearPopulated) {
    return [];
  }

  const specDetails = getSpecInfo(classId, specName) || {};

  const enhancementsBySlot = _.groupBy(_.filter(selectedEnhancements, { specId: specDetails.id }), 'slot');

  // update socket info for later
  let trimmedGear = _.mapValues(gear, items => _.map(items, item => {
    const customItemLimits = [];
    // only allow a single weekly reward item in a combination
    if (item.source === 'weekly_reward') {
      customItemLimits.push({
        category: 'weekly_reward',
        quantity: 1,
      });
    }

    return {
      ...decorateItem(_.omit(item, [
        'chanceBonusList',
        'names',
        'stats',
        'bonusLists',
      ])),
      // assign a unique guid if item doesn't already have one (weapons will have guids)
      // This will be checked later for ring/trinket combos to make sure we don't re-use the same item twice with
      // different gem/enchants
      guid: item.guid || shortid.generate(),
      cacheGuid: shortid.generate(),
      socketInfo: itemSocketInfo(item),
      canRollSocket: canRollSocket(item),
      customItemLimits,
    };
  }));

  const enchantSlotMap = {
    rings: 'finger',
  };

  // setup enchant overrides
  trimmedGear = _.mapValues(trimmedGear, (items, slot) => {
    const enchantSlot = enchantSlotMap[slot] || slot;
    if (enhancementsBySlot[enchantSlot]) {
      const slotItems = [];
      _.each(items, item => {
        if (enhancementReplace || !item.enchant_id) {
          _.each(enhancementsBySlot[enchantSlot], enchant => {
            // don't use makeNewCacheItem here, this is a modification of an existing item
            const newItem = decorateItem({
              ...item,
              enchant_id: enchant.enhancementId,
              override: {
                enchant_id: enchant.enhancementId,
              },
            });
            slotItems.push(newItem);
          });
        } else {
          slotItems.push(item);
        }
      });
      return slotItems;
    }
    return items;
  });

  // add sockets
  const addSocketGemId = _.get(_.find(addSocketGem, { specId: specDetails.id }), 'gemId');
  if (addSocketNum && addSocketGemId) {
    trimmedGear = _.mapValues(trimmedGear, (items) => {
      const slotItems = [];
      _.each(items, item => {
        slotItems.push(item);
        if (canRollSocket(item)) {
          const newItem = makeNewCacheItem(item);
          const gemId = addSocketGemId;
          const newBonusIds = _.split(item.bonus_id, '/');
          newBonusIds.push(1808);
          newItem.bonus_id = newBonusIds.join('/');
          newItem.equipped = false;
          newItem.hasSockets = true;
          newItem.gem_id = gemId;
          newItem.override = {
            ...newItem.override,
            gem_id: gemId,
            addSocket: true,
          };
          // fake an item limit category
          newItem.customItemLimits = [
            ...newItem.customItemLimits,
            {
              category: 'addSocket',
              quantity: addSocketNum,
            },
          ];
          slotItems.push(decorateItem(newItem));
        }
      });
      return slotItems;
    });
  }

  if (!trimmedGear.rings || trimmedGear.rings.length < 2) {
    return [];
  }
  if (!trimmedGear.trinkets || trimmedGear.trinkets.length < 2) {
    return [];
  }

  // figure out valid weapon combos

  if (!trimmedGear.main_hand) {
    return [];
  }


  //-----------------------------------
  // weapon permutations

  const specCanDualWield = specDetails.dualWield;
  const specTwoHandersInOneHand = specDetails.dualWield2h;

  const rawWeaponProduct = Combinatorics.cartesianProduct(trimmedGear.main_hand, [...trimmedGear.off_hand, null]);
  // filter raw weapon product to usable combos

  const weaponPermutations = _.filter(rawWeaponProduct.toArray(), weaponSet => {
    const [mainHand, offHand] = weaponSet;
    const mainHandInfo = getWeaponHands(mainHand);

    // don't re-use the same weapon
    if (offHand && mainHand.guid && offHand.guid && mainHand.guid === offHand.guid) {
      return false;
    }

    // dual wielders should use two weapons
    if (mainHandInfo !== '2h' && specCanDualWield && offHand === null) {
      return false;
    }

    // remove 2h + 1h combos for classes that can't use
    if (mainHandInfo === '2h' && offHand !== null && !specTwoHandersInOneHand) {
      return false;
    }

    // remove empty off hand for fury warriors
    if (specTwoHandersInOneHand && offHand === null) {
      return false;
    }

    // fill both slots
    if (_.includes(['mh', '1h'], mainHandInfo) && offHand === null) {
      return false;
    }

    // reject artifact/non-artifact in same combo
    if (mainHand && offHand) {
      if (mainHand.quality === 6 || offHand.quality === 6) {
        if (mainHand.quality !== offHand.quality) {
          return false;
        }
      }
    }

    return true;
  });

  if (weaponPermutations.length === 0) {
    return [];
  }

  //-----------------------------------
  // generate talent combinations

  // TODO: shift talent combos to getCombinations to avoid reprocessing a bunch of gem filling for better perf

  let talentCombos = null;
  if (selectedTalents && lockedTalents) {
    talentCombos = _.union(
      getTalentCombinations(selectedTalents),
      lockedTalents,
    );
  }

  //-----------------------------------
  // generate azerite essence combinations

  // TODO: shift azerite essence combos to getCombinations to avoid reprocessing a bunch of gem filling for better perf

  const essenceCombos = getEssenceCombinations(selectedEssences, passiveEssences);

  if (essenceCombos && essenceCombos.length === 0) {
    return [];
  }

  //-----------------------------------
  // generate soulbind combos

  const soulbindCombos = getSoulbindCombos(
    soulbinds,
    soulbindGroups,
    conduits,
    conduitRankOverride,
    unlockedSoulbindTraits,
    allSoulbinds,
  );

  //-----------------------------------
  // create the raw cartesian product

  try {
    // get ring and trinket combos, filter out items that got duped because of gem/enchant shenanigans
    const ringCombos = _.reject(
      Combinatorics.bigCombination(trimmedGear.rings, 2).toArray(),
      ([ring1, ring2]) => ring1.guid === ring2.guid,
    );
    if (ringCombos.length === 0) {
      return [];
    }
    const allTrinketsEquipped = _.every(trimmedGear.trinkets, { equipped: true });
    const trinketCombos = _.reduce(
      _.reject(
        Combinatorics.bigCombination(trimmedGear.trinkets, 2).toArray(),
        ([trinket1, trinket2]) => trinket1.guid === trinket2.guid,
      ),
      (result, [trinket1, trinket2]) => {
        result.push([trinket1, trinket2]);
        // make sure double 'on use' trinkets are tested in both slots. First trinket will get more bloodlust time,
        // variations might correspond better with spec cooldowns, etc
        if (!allTrinketsEquipped && trinket1.onUseTrinket && trinket2.onUseTrinket) {
          result.push([{
            ...trinket2,
            equipped: false,
          }, {
            ...trinket1,
            equipped: false,
          }]);
        }
        return result;
      },
      [],
    );

    if (trinketCombos.length === 0) {
      return [];
    }

    const cpArgs = [
      trimmedGear.head,
      trimmedGear.neck,
      trimmedGear.shoulder,
      trimmedGear.back,
      trimmedGear.chest,
      trimmedGear.wrist,
      trimmedGear.hands,
      trimmedGear.waist,
      trimmedGear.legs,
      trimmedGear.feet,
      ringCombos,
      trinketCombos,
      weaponPermutations,
    ];

    if (talentCombos) {
      cpArgs.push(talentCombos);
    }

    if (essenceCombos) {
      cpArgs.push(essenceCombos);
    }

    if (soulbindCombos) {
      cpArgs.push(soulbindCombos);
    }

    /*
    if (cp.length > SharedConfig.optimize.maxRawCombinations) {
      console.log('too many raw combos', cp.length, SharedConfig.optimize.maxRawCombinations);
      return null;
    }
    */

    /*
     * Depth first search of possible gearsets
     * Try to prune branches as quickly as possible rather than doing a full cartesian product and then iterating
     * over everything and filtering things out after-the-fact
     *
     * Takes in a strategy that sorts the slots to experiment to find the best slot order to search.
     * Pruning checks performned:
     *  Reject sets with two unique items
     *  Reject sets that violate category item limits
     *  Reject sets with two unique gems
    */
    let walkCalls = 0;
    let partialsExplored = 0;
    let leavesExplored = 0;
    let tooLarge = false;
    // eslint-disable-next-line no-unused-vars
    let rejections;
    // eslint-disable-next-line no-unused-vars
    let rejectedAt;
    const getCombos = (rawData, {
      sortStrategy = null,
    }) => {
      // TODO: Figure out if there's some smart way to estimate the size here
      const combinations = new Array(10000);
      let nextCombo = 0;
      let calls = 0;
      let partials = 0;
      let leaves = 0;
      const internalRejections = {
        customItemLimits: 0,
        uniqueEquipped: 0,
        itemLimits: 0,
        uniqueGems: 0,
      };
      const internalRejectedAt = _.map(_.range(0, 15), () => 0);

      const maxLen = rawData.length;

      let data;
      if (sortStrategy) {
        const withOrigIdx = _.map(rawData, (el, idx) => ({ data: el, origIdx: idx }));
        data = sortStrategy(withOrigIdx);
      } else {
        data = rawData;
      }

      /*
       * WARNING!
       *
       * This is a heavily optimized piece of code that is easy to screw up.
       * Don't be an idiot, be careful with it.
       */

      const walk = (
        left,
        candidate = [],
        candidateLen = 0,
        uniqueIds = new Set(),
        itemLimitCounts = new Map(),
        uniqueGems = new Set(),
      ) => {
        calls += 1;
        if (
          tooLarge
          || nextCombo > SharedConfig.optimize.maxRawCombinations
          || partials > SharedConfig.optimize.maxWalkPartials
        ) {
          tooLarge = true;
        } else if (candidateLen === maxLen) {
          leaves += 1;
          // Only add sets that are fully socketed or have added a socket
          if (addSocketNum && addSocketGemId) {
            let hasAdded = false;
            let canSocket = 0;
            let socketed = 0;
            for (let i = 0; i < candidateLen; i += 1) {
              const it = candidate[i];
              if (it.override && it.override.addSocket) {
                hasAdded = true;
              }
              if (it.canRollSocket) {
                canSocket += 1;
              }
              if (it.socketInfo && it.socketInfo.total > 0) {
                socketed += 1;
              }
            }
            if (socketed < canSocket && !hasAdded) {
              return;
            }
          }
          combinations[nextCombo] = candidate;
          nextCombo += 1;
        } else {
          const newLeft = left.slice(1);

          for (let i = 0; i < left[0].data.length; i += 1) {
            partials += 1;
            const newItem = left[0].data[i];

            // Warning, heavy optimization ahead!
            // Most of these are empty placeholder, we only copy if we need to make a change
            // This should reduce a huge amount of object copy churn for no real need
            let nextItemLimitCounts;
            let nextUniqueIds;
            let nextUniqueGems;

            if (newItem) {
              // convert to array
              let items;
              if (newItem.length) {
                items = newItem;
              } else {
                items = [newItem];
              }

              let reject = false;

              // rings and trinkets will be two-fer
              for (let j = 0; j < items.length; j += 1) {
                const item = items[j];

                if (!item) {
                  // eslint-disable-next-line no-continue
                  continue;
                }

                /*
                 * Check on various validity constraints. Order here could be very important
                 * as this is a very tight loop that might be executed millions of times so
                 * the first checks should do as much as possible.
                 *
                 * TODO: dynamic ordering of checks based on previous rejections?
                 */

                //////////////////////////////////////////////
                // custom item limit checks
                //////////////////////////////////////////////

                // item limits for things like add socket and preserved contaminant
                if (item.customItemLimits) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }
                  // It's ok to ignore this, reject is checked outside the loop
                  // eslint-disable-next-line no-loop-func
                  for (let limitIdx = 0; limitIdx < item.customItemLimits.length; limitIdx += 1) {
                    const { category, quantity } = item.customItemLimits[limitIdx];
                    const newValue = 1 + (nextItemLimitCounts.get(category) || 0);
                    if (newValue > quantity) {
                      reject = true;
                      internalRejections.customItemLimits += 1;
                      break;
                    }
                    nextItemLimitCounts.set(category, newValue);
                  }
                  if (reject) {
                    break;
                  }
                }


                //////////////////////////////////////////////
                // unique equipped check
                //////////////////////////////////////////////

                // unique equipped
                if (item.uniqueEquipped) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextUniqueIds) {
                    nextUniqueIds = new Set(uniqueIds);
                  }
                  if (nextUniqueIds.has(item.id)) {
                    reject = true;
                    internalRejections.uniqueEquipped += 1;
                    break;
                  } else {
                    nextUniqueIds.add(item.id);
                  }
                }


                //////////////////////////////////////////////
                // item limit check
                //////////////////////////////////////////////

                // item limits
                if (item.itemLimit) {
                  // make sure we only create once in case of ring/trinket loop
                  if (!nextItemLimitCounts) {
                    nextItemLimitCounts = new Map(itemLimitCounts);
                  }

                  const category = item.itemLimit.category;
                  const newValue = 1 + (nextItemLimitCounts.get(category) || 0);

                  if (newValue > item.itemLimit.quantity) {
                    reject = true;
                    internalRejections.itemLimits += 1;
                    break;
                  }
                  nextItemLimitCounts.set(category, newValue);
                }


                //////////////////////////////////////////////
                // unique gem check
                //////////////////////////////////////////////

                // unique gems
                if (item.socketInfo && item.socketInfo.total > 0) {
                  if (item.parsed.gemIsUnique) {
                    if (!nextUniqueGems) {
                      nextUniqueGems = new Set(uniqueGems);
                    }
                    if (nextUniqueGems.has(item.parsed.gem_id)) {
                      reject = true;
                      break;
                    }
                    nextUniqueGems.add(item.parsed.gem_id);
                  }
                }
              }
              if (reject) {
                leaves += 1;
                internalRejectedAt[candidateLen] += 1;
                // eslint-disable-next-line no-continue
                continue;
              }
            }

            const newCandidate = candidate.slice();
            newCandidate[left[0].origIdx] = newItem;

            walk(
              newLeft,
              newCandidate,
              candidateLen + 1,
              // the "next" copies may have never been instantiated if they were never needed
              // in that case, pass along the original
              nextUniqueIds || uniqueIds,
              nextItemLimitCounts || itemLimitCounts,
              nextUniqueGems || uniqueGems,
            );
          }
        }
      };

      walk(data);
      walkCalls = calls;
      partialsExplored = partials;
      leavesExplored = leaves;
      // eslint-disable-next-line no-unused-vars
      rejections = internalRejections;
      // eslint-disable-next-line no-unused-vars
      rejectedAt = internalRejectedAt;

      //devTable(rejectedAt);
      //devTable(rejections);

      return combinations.slice(0, nextCombo);
    };

    // default strategy - fewest slot items first
    const strategies = ['size-asc'];

    /*
    const testStrategies = [
      'resist-then-size-asc',
      'resist-then-size-desc',
    ];

    if (DEVMODE) {
      strategies = testStrategies;
    }
    // */

    let combinations;

    const results = [];
    _.each(strategies, strategyName => {
      let strategy;
      switch (strategyName) {
        case 'size-asc':
          strategy = (rawData) => _.sortBy(rawData, items => items.data.length);
          break;
        case 'size-desc':
          strategy = (rawData) => _.sortBy(rawData, items => -items.data.length);
          break;
        default:
          break;
      }

      const now = getNow();
      combinations = getCombos(cpArgs, {
        sortStrategy: strategy,
      });
      const duration = getNow() - now;

      const cartProductSize = Combinatorics.cartesianProduct(...cpArgs).length;

      results.push({
        strategy: strategyName,
        numCombos: combinations.length.toLocaleString(),
        calls: walkCalls.toLocaleString(),
        leaves: leavesExplored.toLocaleString(),
        explored: partialsExplored.toLocaleString(),
        cpLength: cartProductSize.toLocaleString(),
        pctEvaluated: _.round(100 * (leavesExplored / cartProductSize), 4),
        time: _.round(duration, 1).toLocaleString(),
        timePerExplored: _.round((duration / partialsExplored), 5),
      });
    });


    /*
    const now = getNow();
    cp.toArray();
    const duration = getNow() - now;

    results.push({
      strategy: 'js-combinatorics cartesianProduct',
      numCombos: cp.length,
      calls: null,
      time: _.round(duration, 1),
    });
    */
    //devTable(_.sortBy(results, 'time'));

    if (tooLarge) {
      return Infinity;
    }

    return combinations;

    //return cp;
  } catch (error) {
    // eslint-disable-next-line
    console.error(error);
    return [];
  }
}

const slotIndexMap = [
  'head',
  'neck',
  'shoulder',
  'back',
  'chest',
  'wrist',
  'hands',
  'waist',
  'legs',
  'feet',
  'finger1',
  'finger2',
  'trinket1',
  'trinket2',
  'main_hand',
  'off_hand',
];

let cacheStats;

// ugh, worst function params ever
// This function is also highly optimized and avoids lodash
// All the linting exceptions are intended to try to exit early whenever possible and reduce loops
// over the data.
export function getCombinations(
  cartesianProduct,
  onlyMaxLegendaries,
  tier21Pieces,
  tier20Pieces,
  tier19Pieces,
  lockedRing = null,
  lockedTrinket = null,
  classId,
  spec,
  selectedEnhancements,
  replaceEnhancements,
  forceMainStat,
  equippedGear,
  equippedTalents,
  equippedEssences,
  equippedSoulbind,
  rawCombinationsMax,
) {
  const perfStart = Date.now();

  // rawCombinationMax could be explicit undefined from a selector
  const maxRaw = rawCombinationsMax || SharedConfig.optimize.maxCombinations;

  if (cartesianProduct === null) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct.length === 0) {
    return {
      combinations: [],
    };
  }
  if (cartesianProduct === Infinity) {
    return {
      combinations: null,
      rawCombos: Infinity,
    };
  }

  // things that can make combos > rawCombos (multipliers)
  //  * gems

  // things that can make combos < rawCombos (filters)
  //  * unique equipped
  //  * duplicate gem setups
  //  * duplicate enchant setups
  //  * duplicate azerite power setup

  if (cartesianProduct.length > maxRaw) {
    // generating combos will take significant time.
    // just bail out and use approximations
    return {
      combinations: null,
      rawCombos: cartesianProduct.length,
    };
  }

  let setKeyTime = 0;

  const fulfilledConstraints = {
    uniqueItems: false,
    canFillSockets: true,
  };

  cacheStats = {
    hit: 0,
    miss: 0,
    generateTime: 0,
  };

  const result = [];
  const gemCombinationCache = [];
  const usedItems = new Set();

  const specDetails = getSpecInfo(classId, spec) || {};

  const specSelectedEnhancements = _.filter(selectedEnhancements, { specId: specDetails.id });
  const selectedGems = _.filter(specSelectedEnhancements, { slot: 'socket' });
  const specForceMainStat = _.get(_.find(forceMainStat, { specId: specDetails.id }), 'value', false);
  const setKeys = new Set();


  let tooManyCombos = false;

  const removals = {
    uniqueEquipped: 0,
    itemLimit: 0,
    uniqueGem: 0,
    lockedRing: 0,
    lockedTrinket: 0,
    setKey: 0,
    invalidGemCombo: 0,
  };

  const processGearset = (gearset, equipped = false) => {
    if (tooManyCombos || result.length > SharedConfig.optimize.maxTotalCombinations) {
      if (!tooManyCombos) {
        tooManyCombos = true;
      }
      return false;
    }

    // flatten to just get a simple array of items
    const set = [
      gearset[0],
      gearset[1],
      gearset[2],
      gearset[3],
      gearset[4],
      gearset[5],
      gearset[6],
      gearset[7],
      gearset[8],
      gearset[9],
      gearset[10][0], // ring 1
      gearset[10][1], // ring 2
      gearset[11][0], // trinket 1
      gearset[11][1], // trinket 2
      gearset[12][0],
      gearset[12][1] || {},

      // gearset[13] is selectedTalents
      // gearset[14] is azerite essences
      // gearset[15] is soulbinds
    ];

    const socketSlots = [];
    const uniqueGemsUsed = new Set();

    for (let i = 0; i < set.length; i += 1) {
      const item = set[i];

      if (!item) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (item.socketInfo && item.socketInfo.total > 0) {
        if (replaceEnhancements) {
          socketSlots.push(i);
        } else if (item.socketInfo.filled === 0) {
          socketSlots.push(i);
        } else if (item.parsed.gemIsUnique) {
          if (uniqueGemsUsed.has(item.parsed.gem.id)) {
            return false;
          }
          uniqueGemsUsed.add(item.parsed.gem.id);
        }
      }
    }

    // check for locked trinket and ring
    if (lockedRing !== null) {
      if (set[10].origIdx !== lockedRing && set[11].origIdx !== lockedRing) {
        removals.lockedRing += 1;
        return false;
      }
    }
    if (lockedTrinket !== null && set[12] && set[13]) {
      if (set[12].origIdx !== lockedTrinket && set[13].origIdx !== lockedTrinket) {
        removals.lockedTrinket += 1;
        return false;
      }
    }

    const gear = {
      head: gearset[0],
      neck: gearset[1],
      shoulder: gearset[2],
      back: gearset[3],
      chest: gearset[4],
      wrist: gearset[5],
      hands: gearset[6],
      waist: gearset[7],
      legs: gearset[8],
      feet: gearset[9],
      finger1: gearset[10][0],
      finger2: gearset[10][1],
      trinket1: gearset[11][0],
      trinket2: gearset[11][1],
      main_hand: gearset[12][0],
      off_hand: gearset[12][1],
    };

    // we now have the base gear, time to figure out gem/corruption variation candidates

    const candidates = [];

    ////////////////////////////////////////////////////////////////
    //
    // fill in gem slots
    //


    let gemCombos = [null];
    const numSockets = socketSlots.length;
    if (!equipped) {
      if (numSockets > 0 && specSelectedEnhancements && selectedGems.length > 0) {
        if (!gemCombinationCache[numSockets]) {
          let forceMain = specForceMainStat;
          if (uniqueGemsUsed.size > 0) {
            forceMain = false;
          }
          gemCombinationCache[numSockets] = getGemCombinations(selectedGems, forceMain, numSockets);
        }
        gemCombos = gemCombinationCache[numSockets];
        if (gemCombos.length === 0) {
          fulfilledConstraints.canFillSockets = false;
        }
      }
    }

    for (let i = 0; i < gemCombos.length; i += 1) {
      if (tooManyCombos || result.length > SharedConfig.optimize.maxTotalCombinations) {
        if (!tooManyCombos) {
          tooManyCombos = true;
        }
        return false;
      }

      // get a copy of this array because we gonna mutate it
      const gemCombo = gemCombos[i] && gemCombos[i].slice();

      let comboGear = gear;
      let validGemCombo = true;

      if (gemCombo) {
        // match up gems that already match so we don't just shift gems around
        const slotsToFill = [];
        for (let j = 0; j < numSockets; j += 1) {
          const slotIndex = socketSlots[j];
          const slotName = slotIndexMap[slotIndex];
          const slotGem = parseInt(comboGear[slotName].gem_id, 10);
          const gemComboIdx = _.indexOf(gemCombo, slotGem);
          if (gemComboIdx > -1) {
            // remove gem from combo, already in gear
            gemCombo.splice(gemComboIdx, 1);
          } else {
            slotsToFill.push(slotIndex);
          }
        }

        // fill the rest of the slots
        if (slotsToFill.length > 0) {
          // sort gems and sockets in such a way that highest ilevel items get main stat gem
          /*
          const sortedSlotsToFill = _.sortBy(
            slotsToFill,
            slotIndex => {
              const slotName = slotIndexMap[slotIndex];
              return _.get(comboGear[slotName], 'itemLevel');
            },
          );
          */

          for (let j = 0; j < slotsToFill.length; j += 1) {
            const slotIndex = slotsToFill[j];
            const slotName = slotIndexMap[slotIndex];

            if (uniqueGemsUsed.has(gemCombo[j])) {
              validGemCombo = false;
            }

            // do some careful copying here for perf

            const override = {
              ...comboGear[slotName].override,
              gem_id: String(gemCombo[j]),
            };
            const item = {
              ...makeNewCacheItem(comboGear[slotName]),
              gem_id: String(gemCombo[j]),
              override,
              // create a new cache item
            };
            comboGear = {
              ...comboGear,
              [slotName]: item,
            };
          }
        }
      }

      if (validGemCombo) {
        candidates.push(comboGear);
      } else {
        removals.invalidGemCombo += 1;
      }
    }

    ////////////////////////////////////////////////////////////////
    //
    // De-duplicate new candidate(s) against the global setKeys
    //

    const setKeyStart = getNow();
    _.each(candidates, candidateGear => {
      const overrides = [];
      _.each(candidateGear, (item, slot) => {
        if (item && item.override) {
          overrides.push({
            slot,
            override: item.override,
          });
        }
      });

      let combo;
      // switch for compatibility for sept 26 talent combination update
      if (gearset[13]) {
        // new version, has talents
        let essences;
        let soulbind;
        let covenant;
        if (gearset[14]) {
          if (_.has(gearset[14], 'corruptionReduction')) {
            essences = gearset[14].str;
          } else {
            soulbind = gearset[14].str;
            covenant = gearset[14].covenant;
          }
        }
        combo = {
          gear: candidateGear,
          talents: gearset[13],
          essences,
          overrides: overrides.length > 0 ? overrides : undefined,
          soulbind,
          covenant,
        };
      } else {
        // old version, just gear
        combo = candidateGear;
      }

      ////////////////////////////////////////////////////////////////
      //
      // generate a "key" for this gearset that will remove things like duplicate ring enchants
      // Need to include anything a user can change/select
      //  * selected items
      //  * azerite powers
      //  * weapon enchant
      //  * ring enchants
      //  * talents
      //
      // Gem combos happen after this so we can ignore them in this bit
      //
      // THIS MIGHT BREAK WHEN AZERITE GOES AWAY
      // YOU'LL BE BACK
      //

      const gearCheck = combo.gear;
      if (gearCheck) {
        const selectedIndexes = [];
        const gems = [];
        _.each(gearCheck, item => {
          if (item) {
            selectedIndexes.push(item.selectedIdx);
            if (item.gem_id) {
              gems.push(item.gem_id);
            }
          }
        });

        let soulbindKey = null;
        if (combo.soulbind) {
          const soulbindParts = combo.soulbind.split(',');
          if (soulbindParts[1]) {
            soulbindKey = soulbindParts[1].split('/').sort().join('/');
          }
        }

        const setKey = [
          // item IDs
          selectedIndexes.join('/'),
          // azerite powers
          [
            ...(gearCheck.head ? gearCheck.head.parsed.azerite_powers : [0]),
            ...(gearCheck.shoulder ? gearCheck.shoulder.parsed.azerite_powers : [0]),
            ...(gearCheck.chest ? gearCheck.chest.parsed.azerite_powers : [0]),
          ].sort().join('/'),

          // enchants
          (gearCheck.main_hand && gearCheck.main_hand.parsed && gearCheck.main_hand.parsed.enchant_id) || 0,
          (gearCheck.off_hand && gearCheck.off_hand.parsed && gearCheck.off_hand.parsed.enchant_id) || 0,
          [
            (gearCheck.finger1 && gearCheck.finger1.parsed && gearCheck.finger1.parsed.enchant_id) || 0,
            (gearCheck.finger2 && gearCheck.finger2.parsed && gearCheck.finger2.parsed.enchant_id) || 0,
          ].sort().join('/'), // sort these which will remove dupes
          (gearCheck.back && gearCheck.back.parsed && gearCheck.back.parsed.enchant_id) || 0,
          (gearCheck.hands && gearCheck.hands.parsed && gearCheck.hands.parsed.enchant_id) || 0,
          (gearCheck.chest && gearCheck.chest.parsed && gearCheck.chest.parsed.enchant_id) || 0,
          (gearCheck.wrist && gearCheck.wrist.parsed && gearCheck.wrist.parsed.enchant_id) || 0,
          (gearCheck.feet && gearCheck.feet.parsed && gearCheck.feet.parsed.enchant_id) || 0,

          // gems
          gems.sort().join('/'),

          combo.talents,
          combo.essences,
          soulbindKey,
        ].join('|');

        // add combination if not already seen
        if (!setKeys.has(setKey)) {
          setKeys.add(setKey);

          // add items to usedItems cache
          _.each(combo.gear, (item, slot) => {
            if (item) {
              usedItems.add(`${slot}:${item.selectedIdx}`);
            }
          });

          result.push(combo);
        } else {
          removals.setKey += 1;
        }
      }
    });
    setKeyTime += getNow() - setKeyStart;

    return true;
  };

  // only prepend equipped if frontend sent it
  if (equippedGear) {
    let expansionSlot;
    if (equippedSoulbind) {
      const soulbind = parseSoulbindSimcString(equippedSoulbind);
      expansionSlot = {
        str: equippedSoulbind,
        path: '',
        covenant: getCovenant(soulbind?.covenantId),
      };
    } else if (equippedEssences) {
      expansionSlot = {
        str: stringifyAzeriteEssences(parseAzeriteEssences(equippedEssences)),
        corruptionReduction: 0,
      };
    }
    const equipped = [
      decorateItem(equippedGear.head[0]),
      decorateItem(equippedGear.neck[0]),
      decorateItem(equippedGear.shoulder[0]),
      decorateItem(equippedGear.back[0]),
      decorateItem(equippedGear.chest[0]),
      decorateItem(equippedGear.wrist[0]),
      decorateItem(equippedGear.hands[0]),
      decorateItem(equippedGear.waist[0]),
      decorateItem(equippedGear.legs[0]),
      decorateItem(equippedGear.feet[0]),
      _.map(equippedGear.rings, decorateItem),
      _.map(equippedGear.trinkets, decorateItem),
      _.map([equippedGear.main_hand[0], equippedGear.off_hand[0]], decorateItem),
      equippedTalents,
      expansionSlot,
    ];

    processGearset(equipped, true);
  }

  cartesianProduct.forEach(gearset => processGearset(gearset, false));

  ////////////////////////////////////////////////////////////////
  //
  // finish up
  //

  // unable to make combos because of a gem issue
  if (result.length === 0) {
    fulfilledConstraints.canFillSockets = false;
  }

  const perfEnd = Date.now();
  const duration = perfEnd - perfStart;

  if (tooManyCombos) {
    return {
      combinations: null,
      fulfilledConstraints,
      removals,
      stats: null,
    };
  }

  const stats = {
    rawCombos: cartesianProduct.length,
    filteredCombos: result.length,
    time: duration,
    cacheStats,
    setKeyTime,
  };

  /*
  console.table(_.reverse(_.sortBy(_.mapValues(timings, (timing, key) => ({
    key,
    time: _.round(timing, 1),
    pct: _.round(100 * (timing / duration), 1),
  })), 'time')));
  */
  //console.log(stats);

  const slotMap = {
    finger1: 'rings',
    finger2: 'rings',
    trinket1: 'trinkets',
    trinket2: 'trinkets',
  };

  return {
    combinations: result,
    fulfilledConstraints,
    removals,
    stats,
    usedItems: _.map([...usedItems], key => {
      const parts = _.split(key, ':');
      return {
        slot: slotMap[parts[0]] || parts[0],
        index: parseInt(parts[1], 10),
      };
    }),
  };
}
